import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { interactionAPI, customerAPI } from "../lib/api";
import { formatDateTime, formatDate } from "../lib/utils";
import { Clock, User, MapPin, MessageSquare, Edit2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const INTERACTION_TYPES = {
  phone_call: "Phone Call",
  email: "Email", 
  meeting: "Meeting",
  support: "Support",
  sales: "Sales"
};

export function PendingJobs() {
  const { user } = useAuth();
  const [pendingJobs, setPendingJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [editData, setEditData] = useState({
    notes: "",
    pending_job: true
  });

  useEffect(() => {
    loadPendingJobs();
  }, []);

  const loadPendingJobs = async () => {
    try {
      setLoading(true);
      const [pendingRes, customersRes] = await Promise.all([
        interactionAPI.getPendingJobs(),
        customerAPI.getAll()
      ]);
      setPendingJobs(pendingRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error("Failed to load pending jobs");
      console.error("Load pending jobs error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : "Unknown Customer";
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      phone_call: "bg-green-100 text-green-800",
      email: "bg-blue-100 text-blue-800",
      meeting: "bg-purple-100 text-purple-800",
      support: "bg-orange-100 text-orange-800",
      sales: "bg-yellow-100 text-yellow-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const handleEditJob = (interaction) => {
    setEditingInteraction(interaction);
    setEditData({
      notes: "",
      pending_job: interaction.pending_job
    });
    setShowEditDialog(true);
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    
    if (!editData.notes.trim() && editData.pending_job === editingInteraction.pending_job) {
      toast.error("Please add notes or change the pending job status");
      return;
    }

    try {
      const updatePayload = {};
      
      if (editData.notes.trim()) {
        updatePayload.notes = editData.notes;
      }
      
      if (editData.pending_job !== editingInteraction.pending_job) {
        updatePayload.pending_job = editData.pending_job;
      }

      await interactionAPI.update(editingInteraction.id, updatePayload);
      toast.success("Job updated successfully");
      setShowEditDialog(false);
      setEditingInteraction(null);
      loadPendingJobs();
    } catch (error) {
      toast.error("Failed to update job");
      console.error("Update job error:", error);
    }
  };

  const handleMarkComplete = async (interaction) => {
    try {
      await interactionAPI.update(interaction.id, { pending_job: false });
      toast.success("Job marked as complete");
      loadPendingJobs();
    } catch (error) {
      toast.error("Failed to mark job as complete");
      console.error("Mark complete error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="pending-jobs-title">
            Pending Jobs
          </h1>
          <p className="text-slate-600 mt-1">Track and manage all pending customer jobs</p>
        </div>
        
        <Button
          onClick={loadPendingJobs}
          variant="outline"
          data-testid="refresh-pending-jobs-button"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Pending Jobs</CardTitle>
            <Clock className="w-4 h-4 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingJobs.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Onsite Jobs</CardTitle>
            <MapPin className="w-4 h-4 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {pendingJobs.filter(job => job.is_onsite_support).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Remote Jobs</CardTitle>
            <MessageSquare className="w-4 h-4 text-slate-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {pendingJobs.filter(job => !job.is_onsite_support).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Jobs List */}
      {pendingJobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-testid="no-pending-jobs-title">
              No pending jobs
            </h3>
            <p className="text-slate-600">
              Great! All jobs have been completed.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {pendingJobs.map((job) => (
            <Card key={job.id} className="card-hover" data-testid={`pending-job-${job.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Badge className={getTypeBadgeColor(job.type)}>
                      {INTERACTION_TYPES[job.type] || job.type}
                    </Badge>
                    {job.is_onsite_support && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        Onsite
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditJob(job)}
                      data-testid={`edit-job-${job.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkComplete(job)}
                      className="text-green-600 hover:text-green-700"
                      data-testid={`complete-job-${job.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardTitle className="text-lg">
                  {getCustomerName(job.customer_id)}
                </CardTitle>
                <CardDescription>
                  <div className="flex items-center text-sm text-slate-600">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDateTime(job.date)}
                  </div>
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  {job.notes && (
                    <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                      <strong>Notes:</strong><br />
                      {job.notes}
                    </div>
                  )}
                  
                  {job.start_time && job.end_time && (
                    <div className="text-xs text-slate-500">
                      Support Time: {formatDateTime(job.start_time)} - {formatDateTime(job.end_time)}
                    </div>
                  )}
                  
                  <div className="text-xs text-slate-500 pt-2 border-t">
                    Created: {formatDate(job.created_date)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Job Dialog */}
      {showEditDialog && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Job</DialogTitle>
              <DialogDescription>
                Add additional notes or mark job as complete
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateJob} className="space-y-4">
              <div>
                <Label htmlFor="additional-notes">Additional Notes</Label>
                <Textarea
                  id="additional-notes"
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Add any updates or additional information..."
                  rows={4}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-pending-job"
                  checked={editData.pending_job}
                  onChange={(e) => setEditData({ ...editData, pending_job: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <Label htmlFor="edit-pending-job">Keep as pending job</Label>
              </div>
              
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">
                  Update Job
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}